import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';
import Grid from '../../Grid';
import Keyboard from '../../Keyboard';
import GameImage from '../unlimited/GameImage';
import { useProfile } from '../../../contexts/ProfileContext';
import { useDebouncedCallback } from '../../../utils/hooks/useDebouncedCallback';
import { generateFalImageForWord } from '../../../utils/generationsApi';
import { useGenerationsRoundBuffer } from '../../../utils/hooks/useGenerationsRoundBuffer';
import {
  clearSavedGenerationsGameDb,
  getSavedGenerationsGameDb,
  saveGenerationsGameDb,
} from '../../../utils/games/generationsGameStorageDb';
import { spendCoins } from '../../../utils/coinManager';
import { requestPowerup } from '../../../utils/powerupApi';
import { fetchRiddle } from '../../../utils/riddleApi';
import { getRiddleNextCost, RIDDLE_DIFFICULTY } from '../../../utils/riddlePricing';
import { getLossRewardCoins, getWinRewardCoins } from '../../../economy/economyCatalog';
import {
  DEFAULT_GENERATIONS_STATS,
  ERROR_DISPLAY_TIME,
  GENERATIONS_IMAGE_COST,
  GENERATIONS_SKIP_COST,
  GENERATIONS_MIN_COINS_TO_BUFFER_SKIP,
  INITIAL_GAME_STATE,
  MIN_ZOOM,
  WIN_MODAL_DELAY,
  ZOOM_DECREASE,
} from '../../../constants/games/generationsGame';
import { generateRandomZoomOrigin } from '../../../utils/zoomPosition';
import GenerationsGameModal from './GenerationsGameModal';
import RiddlePanel from '../unlimited/RiddlePanel';
import GenerationsGameControls from './GenerationsGameControls';
import ImagePreviewModal from '../shared/ImagePreviewModal';
import '../../../styles/gamePolish.css';
import './GenerationsLayout.css';
import './GenerationsMobileLayout.css';
import { genDebug, isGenerationsDebugEnabled } from '../../../utils/debug/generationsDebug';
import { localDevWarn } from '../../../utils/debug/localDevLogger';
import { useActiveTimer } from '../../../utils/hooks/useActiveTimer';
import { startRound, finishRound } from '../../../utils/gameRoundApi';

const COUNTDOWN_TIME = 5;
const GENERATIONS_STATS_STORAGE_KEY = 'zoomedInGenerationsStats';

const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];
const normalizeGenerationsStats = (stats) => {
  const toCount = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    return Math.floor(parsed);
  };

  return {
    guesses: {
      1: toCount(stats?.guesses?.[1]),
      2: toCount(stats?.guesses?.[2]),
      3: toCount(stats?.guesses?.[3]),
      4: toCount(stats?.guesses?.[4]),
      5: toCount(stats?.guesses?.[5]),
    },
    losses: toCount(stats?.losses),
    currentStreak: toCount(stats?.currentStreak),
    maxStreak: toCount(stats?.maxStreak),
  };
};

const ZoomedInGenerationsGame = ({ mobileSideBySide = false }) => {
  const navigate = useNavigate();
  const { user, loading: profileLoading, coins, refreshCoins, profile, setSeasonPoints, setLifetimePoints } = useProfile();
  const coinsSafe = typeof coins === 'number' ? coins : 0;
  const canAfford = coinsSafe >= GENERATIONS_IMAGE_COST;
  const canAffordBufferSkip = coinsSafe >= GENERATIONS_MIN_COINS_TO_BUFFER_SKIP;

  const [validWords, setValidWords] = useState(new Set());
  const [solutionPool, setSolutionPool] = useState([]);
  const [dictionaryLoaded, setDictionaryLoaded] = useState(false);

  const [gameState, setGameState] = useState({ ...INITIAL_GAME_STATE });
  const [usedLetters, setUsedLetters] = useState(new Set());
  const [imageLoading, setImageLoading] = useState(false);
  const [errorState, setErrorState] = useState({ showError: false, errorMessage: '' });
  const [confettiState, setConfettiState] = useState({ showConfetti: false });
  const [modalState, setModalState] = useState({
    showModal: false,
    title: '',
    isWin: false,
    reportRoundId: null,
  });
  const [countdown, setCountdown] = useState(0);
  const mounted = useRef(true);
  const [freeInspectsRemaining, setFreeInspectsRemaining] = useState(0);
  const [freeZoomOutsRemaining, setFreeZoomOutsRemaining] = useState(0);
  const [isInspecting, setIsInspecting] = useState(false);
  const [isZoomingOut, setIsZoomingOut] = useState(false);
  const zoomOutTimerRef = useRef(null);
  const rewardGuardRef = useRef({ key: null, rewarded: false });
  const roundIdRef = useRef(null);
  const activeTimer = useActiveTimer();
  const [hasRevealableLetters, setHasRevealableLetters] = useState(true);
  const [restoredBuffer, setRestoredBuffer] = useState(null);
  // Synchronous flag: true while a saved buffer is being restored from DB but hasn't
  // propagated through the hook yet. Prevents the prefetch effect from starting a
  // duplicate (paid) generation during the 1-2 render gap.
  const pendingBufferRestoreRef = useRef(false);

  // Definition state (same UX pattern as Unlimited's win modal)
  const [showDefinition, setShowDefinition] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [isLoadingDefinition, setIsLoadingDefinition] = useState(false);

  // Riddle state (same UX pattern as Unlimited: button in controls + shared RiddlePanel)
  const [showRiddlePanel, setShowRiddlePanel] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [riddleText, setRiddleText] = useState('');
  const [riddleHistory, setRiddleHistory] = useState([]);
  const [riddleError, setRiddleError] = useState('');
  const [isLoadingRiddle, setIsLoadingRiddle] = useState(false);
  const [riddleUseCount, setRiddleUseCount] = useState(0);
  const [serverNotEnoughFor, setServerNotEnoughFor] = useState(null); // 'hard' | 'easy' | null
  const [freeEasyRiddlesRemaining, setFreeEasyRiddlesRemaining] = useState(0);
  const [isRestoreCheckPending, setIsRestoreCheckPending] = useState(true);
  const [stats, setStats] = useState(DEFAULT_GENERATIONS_STATS);

  // Keep DB payloads bounded (riddles can be long).
  const capRiddleHistory = useCallback((arr) => {
    if (!Array.isArray(arr)) return [];
    const MAX_RIDDLES_TO_PERSIST = 20;
    if (arr.length <= MAX_RIDDLES_TO_PERSIST) return arr;
    return arr.slice(arr.length - MAX_RIDDLES_TO_PERSIST);
  }, []);

  const showErrorMessage = useCallback((message) => {
    setErrorState({ showError: true, errorMessage: message });
    setTimeout(() => {
      setErrorState((prev) => ({ ...prev, showError: false }));
    }, ERROR_DISPLAY_TIME);
  }, []);

  const openImagePreview = useCallback((clickedSrc) => {
    if (!clickedSrc) return;
    setPreviewImageUrl(clickedSrc);
  }, []);

  const closeImagePreview = useCallback(() => {
    setPreviewImageUrl('');
  }, []);

  // Load word lists:
  // - solutionPool comes from public five-letter list (same as build/fiveletterlist.txt after build)
  // - validWords (guess validation) comes from public/wordlistdictionary.txt
  useEffect(() => {
    const loadWordLists = async () => {
      try {
        const [solutionsRes, dictionaryRes] = await Promise.all([
          fetch('/fiveletterlist.txt'),
          fetch('/wordlistdictionary.txt'),
        ]);

        if (!solutionsRes.ok) throw new Error('Failed to load fiveletterlist.txt');
        if (!dictionaryRes.ok) throw new Error('Failed to load wordlistdictionary.txt');

        const [solutionsText, dictionaryText] = await Promise.all([
          solutionsRes.text(),
          dictionaryRes.text(),
        ]);

        const parseFiveLetterWords = (text) =>
          text
            .split('\n')
            .map((w) => w.trim().toLowerCase())
            .filter((w) => w.length === 5 && /^[a-z]{5}$/.test(w));

        const solutionWords = Array.from(new Set(parseFiveLetterWords(solutionsText)));
        const dictionaryWords = parseFiveLetterWords(dictionaryText);

        // Important: include solution words in validWords so the solution is always guessable
        // even if it's missing from the bigger dictionary file.
        setValidWords(new Set([...dictionaryWords, ...solutionWords]));
        setSolutionPool(solutionWords);
        setDictionaryLoaded(true);
      } catch (error) {
        console.error('Error loading word lists:', error);
        showErrorMessage('Failed to load word lists.');
      }
    };

    loadWordLists();
  }, [showErrorMessage]);

  useEffect(() => {
    // New word => new riddle "session" (UI reset).
    // Important: keep this scoped to `solution` changes only, otherwise
    // saving a newly generated riddle into `riddleSession` would close the panel.
    setShowRiddlePanel(false);
    setRiddleError('');
    setIsLoadingRiddle(false);
    setServerNotEnoughFor(null);
  }, [gameState.solution]);

  useEffect(() => {
    const solutionLower = String(gameState.solution || '').toLowerCase();
    const sessionFromDbState = gameState?.riddleSession;
    const hasDbSessionForWord =
      sessionFromDbState &&
      String(sessionFromDbState.word || '').toLowerCase() === solutionLower &&
      Array.isArray(sessionFromDbState.riddles);

    if (hasDbSessionForWord) {
      const nextRiddles = capRiddleHistory(sessionFromDbState.riddles).filter(Boolean);
      const nextUseCount = Number.isFinite(sessionFromDbState.useCount) ? sessionFromDbState.useCount : 0;
      setRiddleHistory(nextRiddles);
      setRiddleUseCount(nextUseCount);
      setRiddleText(nextRiddles?.[nextRiddles.length - 1] || '');
      return;
    }

    // No persisted session for this word: reset.
    setRiddleText('');
    setRiddleHistory([]);
    setRiddleUseCount(0);

    // Prevent accidentally showing a previous word's session after resume.
    if (sessionFromDbState && sessionFromDbState.word && !hasDbSessionForWord) {
      setGameState((prev) => ({ ...prev, riddleSession: null }));
    }
  }, [capRiddleHistory, gameState.riddleSession, gameState.solution]);

  useEffect(() => {
    // If coins change (e.g. after refresh), clear sticky "not enough" so user can retry.
    setServerNotEnoughFor(null);
  }, [coinsSafe]);

  useEffect(() => {
    setPreviewImageUrl('');
  }, [gameState.imageUrl]);

  const nextRiddleCostNormal = useMemo(
    () =>
      getRiddleNextCost({
        useCount: riddleUseCount,
        difficulty: RIDDLE_DIFFICULTY.HARD,
        guessRow: gameState.currentRow,
      }),
    [riddleUseCount, gameState.currentRow]
  );
  const nextRiddleCostEasy = useMemo(
    () =>
      getRiddleNextCost({
        useCount: riddleUseCount,
        difficulty: RIDDLE_DIFFICULTY.EASY,
        guessRow: gameState.currentRow,
      }),
    [riddleUseCount, gameState.currentRow]
  );

  const canAffordNormalRiddle =
    serverNotEnoughFor !== RIDDLE_DIFFICULTY.HARD && coinsSafe >= nextRiddleCostNormal;
  const canAffordEasyRiddle =
    serverNotEnoughFor !== RIDDLE_DIFFICULTY.EASY &&
    (freeEasyRiddlesRemaining > 0 || coinsSafe >= nextRiddleCostEasy);

  // Restore persisted game (signed-in users only).
  useEffect(() => {
    mounted.current = true;
    const restore = async () => {
      if (!mounted.current) return;
      if (profileLoading) return;
      if (!dictionaryLoaded) return;
      setIsRestoreCheckPending(true);
      if (!user?.id) {
        // Signed-in only mode; show prompt instead of restoring guest state.
        setGameState({ ...INITIAL_GAME_STATE });
        setUsedLetters(new Set());
        setRestoredBuffer(null);
        setIsRestoreCheckPending(false);
        return;
      }

      try {
        const saved = await getSavedGenerationsGameDb(user.id);
        genDebug('restore:read_db', {
          hasSaved: Boolean(saved),
          userId: user.id,
        });
        if (!saved) return;

        // New shape: { gameState, bufferedRound }
        const { gameState: savedGameState, bufferedRound: savedBuffer } = saved;
        const { usedLetters: savedUsedLetters, ...rest } = savedGameState || {};
        genDebug('restore:apply_state', {
          hasSolution: Boolean(rest?.solution),
          hasImageUrl: Boolean(rest?.imageUrl),
          gameOver: Boolean(rest?.gameOver),
          hasSavedBuffer: Boolean(savedBuffer?.word && savedBuffer?.imageUrl),
          savedBufferWord: savedBuffer?.word || null,
        });
        setGameState((prev) => ({
          ...prev,
          ...rest,
          loading: false,
          isChecking: false,
          isRequestPending: false,
        }));
        if (Array.isArray(savedUsedLetters)) {
          setUsedLetters(new Set(savedUsedLetters));
        }
        // Restore buffer so Skip is instant after app resume.
        // Set the ref SYNCHRONOUSLY before the async state update so the prefetch
        // effect (which runs after the next render) knows a buffer is incoming.
        if (savedBuffer?.word && savedBuffer?.imageUrl) {
          pendingBufferRestoreRef.current = true;
          setRestoredBuffer(savedBuffer);
        }
        setImageLoading(false);
        if (rest?.gameOver) {
          setModalState({
            showModal: true,
            title: rest?.guesses?.includes(rest?.solution) ? 'Congrats, You Win!' : 'Round Over',
            isWin: rest?.guesses?.includes(rest?.solution),
            reportRoundId: null,
          });
        } else if (rest?.solution && rest?.imageUrl) {
          // Restored rounds need fresh tracking because timer/ref state resets on reload.
          activeTimer.start();
          startRound({ gameType: 'generations' }).then((id) => {
            roundIdRef.current = id;
          });
        }
      } catch (error) {
        console.error('Error restoring generations game:', error);
      } finally {
        if (mounted.current) {
          setIsRestoreCheckPending(false);
        }
      }
    };

    restore();
    return () => {
      mounted.current = false;
      if (zoomOutTimerRef.current) {
        clearTimeout(zoomOutTimerRef.current);
        zoomOutTimerRef.current = null;
      }
    };
  }, [dictionaryLoaded, profileLoading, user?.id]);

  // Track latest buffer for persistence (avoid stale closure in debounced persist).
  const latestBufferRef = useRef(null);

  const handleBufferChange = useCallback((buffer) => {
    latestBufferRef.current = buffer;
  }, []);

  const {
    bufferedRound,
    isPrefetching,
    prefetchNext,
    consumeBufferedRound,
    clearBuffer,
  } = useGenerationsRoundBuffer({
    enabled: Boolean(user?.id) && !profileLoading,
    solutionPool,
    excludeWord: gameState.solution,
    initialBuffer: restoredBuffer,
    onPrefetchStart: () => {
      if (!mounted.current) return;
      setGameState((prev) => ({ ...prev, isPrefetchPending: true }));
    },
    onPrefetchEnd: () => {
      if (!mounted.current) return;
      setGameState((prev) => ({ ...prev, isPrefetchPending: false }));
    },
    onPrefetchError: (error) => {
      // Don't spam errors for background buffer; just log and keep buffer empty.
      localDevWarn('[Generations] prefetch failed:', error);
    },
    onBufferChange: handleBufferChange,
  });

  // Keep ref in sync with bufferedRound (for persistence).
  // Also clear the "pending restore" flag once the hook has synced the buffer.
  // This effect is defined BEFORE the prefetch effect, so React runs it first in
  // the same commit — by the time the prefetch effect executes, the flag is cleared.
  useEffect(() => {
    latestBufferRef.current = bufferedRound;
    if (pendingBufferRestoreRef.current && bufferedRound) {
      genDebug('buffer_restore:synced', { word: bufferedRound.word });
      pendingBufferRestoreRef.current = false;
    }
  }, [bufferedRound]);

  const { debounced: persistDebounced, cancel: cancelPersistDebounced } = useDebouncedCallback(
    async ({ userId, gameStateSnapshot, usedLettersSnapshot, bufferSnapshot }) => {
      if (!userId) return;
      if (!gameStateSnapshot?.solution) return;
      await saveGenerationsGameDb(userId, gameStateSnapshot, usedLettersSnapshot, bufferSnapshot);
    },
    900
  );

  // Persist debounced (includes buffer so it survives app backgrounding).
  useEffect(() => {
    if (!user?.id) return;
    if (!gameState.solution || gameState.isRequestPending) {
      cancelPersistDebounced();
      return;
    }
    persistDebounced({
      userId: user.id,
      gameStateSnapshot: gameState,
      usedLettersSnapshot: usedLetters,
      bufferSnapshot: latestBufferRef.current,
    });
  }, [user?.id, gameState, usedLetters, bufferedRound, persistDebounced, cancelPersistDebounced]);

  // Modal countdown.
  useEffect(() => {
    if (modalState.showModal) setCountdown(COUNTDOWN_TIME);
  }, [modalState.showModal]);
  useEffect(() => {
    if (modalState.showModal && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [modalState.showModal, countdown]);

  const openAuthModal = useCallback(() => {
    // Members-only mode: send signed-out users to the dedicated auth page.
    navigate('/auth');
  }, [navigate]);

  const handleMaybeLater = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    if (profileLoading) return;

    if (!user) {
      setStats(DEFAULT_GENERATIONS_STATS);
      return;
    }

    try {
      const saved = localStorage.getItem(GENERATIONS_STATS_STORAGE_KEY);
      if (!saved) {
        setStats(DEFAULT_GENERATIONS_STATS);
        return;
      }
      const parsed = JSON.parse(saved);
      setStats(normalizeGenerationsStats(parsed));
    } catch (error) {
      console.error('Error loading generations stats:', error);
      setStats(DEFAULT_GENERATIONS_STATS);
    }
  }, [profileLoading, user]);

  useEffect(() => {
    if (profileLoading) return;
    if (!user) {
      setFreeInspectsRemaining(0);
      setFreeZoomOutsRemaining(0);
      setFreeEasyRiddlesRemaining(0);
      return;
    }
    setFreeInspectsRemaining(profile?.free_inspects_remaining ?? 0);
    setFreeZoomOutsRemaining(profile?.free_zoom_outs_remaining ?? 0);
    setFreeEasyRiddlesRemaining(profile?.free_easy_riddles_remaining ?? 0);
  }, [user, profile, profileLoading]);

  const checkForRevealableLetters = useCallback(() => {
    if (!gameState.solution) return false;
    const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const usedLettersArray = Array.from(usedLetters).map((status) => status.split('-')[0]);
    const unusedLetters = allLetters.filter((letter) => !usedLettersArray.includes(letter));
    const solutionLetters = gameState.solution.toUpperCase().split('');
    const absentLetters = unusedLetters.filter((letter) => !solutionLetters.includes(letter));
    return absentLetters.length > 0;
  }, [usedLetters, gameState.solution]);

  useEffect(() => {
    setHasRevealableLetters(checkForRevealableLetters());
  }, [usedLetters, gameState.solution, checkForRevealableLetters]);

  // Whenever a round is fully loaded, start buffering the next round in the background.
  useEffect(() => {
    if (!user?.id) return;
    if (profileLoading) return;
    if (!dictionaryLoaded) return;
    if (!gameState.solution || !gameState.imageUrl) return;
    if (gameState.isRequestPending) return;
    if (!canAffordBufferSkip) return;
    // Guard: if we're restoring a saved buffer from DB, the hook needs one more
    // render cycle to sync it into `bufferedRound`. Without this, we'd fire a
    // duplicate (paid) generation for a buffer that already exists.
    if (pendingBufferRestoreRef.current) {
      genDebug('prefetch_effect:skipped_pending_restore');
      return;
    }
    // Fire-and-forget: the hook handles concurrency and "already buffered" cases.
    genDebug('prefetch_effect:trigger', {
      userId: user.id,
      hasSolution: Boolean(gameState.solution),
      hasImageUrl: Boolean(gameState.imageUrl),
      coinsSafe,
      canAffordBufferSkip,
      hasBufferedRound: Boolean(bufferedRound),
      isPrefetching,
      isPrefetchPending: Boolean(gameState.isPrefetchPending),
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    });
    prefetchNext();
  }, [
    canAffordBufferSkip,
    dictionaryLoaded,
    gameState.imageUrl,
    gameState.isRequestPending,
    gameState.solution,
    prefetchNext,
    profileLoading,
    user?.id,
    coinsSafe,
    bufferedRound,
    isPrefetching,
    gameState.isPrefetchPending,
  ]);

  // Debug: observe when the tab/window is backgrounded/foregrounded.
  useEffect(() => {
    if (!isGenerationsDebugEnabled()) return;

    const log = (source) => {
      genDebug('visibility', {
        source,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        hidden: typeof document !== 'undefined' ? document.hidden : undefined,
        userId: user?.id || null,
        coinsSafe,
        hasSolution: Boolean(gameState.solution),
        hasImageUrl: Boolean(gameState.imageUrl),
        hasBufferedRound: Boolean(bufferedRound),
        isPrefetching,
        isPrefetchPending: Boolean(gameState.isPrefetchPending),
      });
    };

    const onVis = () => log('document.visibilitychange');
    const onFocus = () => log('window.focus');
    const onBlur = () => log('window.blur');

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    // Initial snapshot
    log('mount');

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [
    bufferedRound,
    coinsSafe,
    gameState.imageUrl,
    gameState.isPrefetchPending,
    gameState.solution,
    isPrefetching,
    user?.id,
  ]);

  const resetRoundUi = useCallback(async () => {
    if (!user?.id) return;
    cancelPersistDebounced();
    // Clear persisted state up-front so we don't keep showing an old round on refresh
    // if anything fails midway.
    await clearSavedGenerationsGameDb(user.id);
    setModalState({ showModal: false, title: '', isWin: false, reportRoundId: null });
    setConfettiState({ showConfetti: false });
    setUsedLetters(new Set());
    setImageLoading(true);

    // Reset definition-related state when starting a new round
    setDefinition(null);
    setShowDefinition(false);
    setIsLoadingDefinition(false);
  }, [cancelPersistDebounced, user?.id]);

  // Function to fetch word definition (dictionaryapi.dev)
  const fetchDefinition = useCallback(async (word) => {
    if (!word) return;
    setIsLoadingDefinition(true);
    try {
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`
      );
      if (!response.ok) throw new Error('Definition not found');
      const data = await response.json();
      setDefinition(data);
    } catch (error) {
      console.error('Error fetching definition:', error);
      setDefinition(null);
    } finally {
      setIsLoadingDefinition(false);
    }
  }, []);

  // Function to toggle definition
  const toggleDefinition = useCallback(() => {
    if (!gameState.solution) return;
    if (!showDefinition) {
      // Always fetch a fresh definition when opening
      fetchDefinition(gameState.solution);
    }
    setShowDefinition((prev) => !prev);
  }, [fetchDefinition, gameState.solution, showDefinition]);

  const applyRound = useCallback((round) => {
    if (!round?.word || !round?.imageUrl) return;
    setGameState((prev) => ({
      ...INITIAL_GAME_STATE,
      // Preserve current prefetch flag; the hook will update it as needed.
      isPrefetchPending: prev.isPrefetchPending,
      solution: round.word,
      imageUrl: round.imageUrl,
      zoomOrigin: generateRandomZoomOrigin(),
      loading: false,
      isRequestPending: false,
    }));

    // Leaderboard: start round tracking (fire-and-forget)
    activeTimer.start();
    startRound({ gameType: 'generations' }).then((id) => {
      roundIdRef.current = id;
    });
  }, [activeTimer]);

  const generateAndStartRound = useCallback(async () => {
    if (gameState.isRequestPending) return;
    if (isRestoreCheckPending) return;
    if (profileLoading) return;
    if (!user) {
      openAuthModal();
      return;
    }
    if (!dictionaryLoaded || solutionPool.length === 0) {
      showErrorMessage('Dictionary not loaded yet.');
      return;
    }
    if (!canAfford) {
      showErrorMessage('Not enough coins to generate an image.');
      return;
    }

    clearBuffer();
    await resetRoundUi();
    const word = pickRandom(solutionPool);

    setGameState((prev) => ({
      ...prev,
      ...INITIAL_GAME_STATE,
      loading: true,
      isRequestPending: true,
      isPrefetchPending: prev.isPrefetchPending,
      solution: word,
      zoomOrigin: generateRandomZoomOrigin(),
    }));

    try {
      const data = await generateFalImageForWord({ word });
      if (!mounted.current) return;

      applyRound({ word: data.word, imageUrl: data.imageUrl });
      setImageLoading(false);
      await refreshCoins?.();
    } catch (error) {
      console.error('Error generating image:', error);
      if (!mounted.current) return;
      setGameState((prev) => ({ ...prev, loading: false, isRequestPending: false }));
      setImageLoading(false);
      if (error?.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins to generate an image.');
      } else if (error?.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else if (error?.code === 'FAL_TIMEOUT') {
        showErrorMessage('Generation timed out. Try again.');
      } else {
        showErrorMessage('Failed to generate image. Try again.');
      }
    }
  }, [
    applyRound,
    canAfford,
    clearBuffer,
    dictionaryLoaded,
    gameState.isRequestPending,
    isRestoreCheckPending,
    openAuthModal,
    profileLoading,
    refreshCoins,
    resetRoundUi,
    showErrorMessage,
    solutionPool,
    user,
  ]);

  const nextRound = useCallback(async () => {
    if (gameState.isRequestPending) return;
    if (profileLoading) return;
    if (!user) {
      openAuthModal();
      return;
    }

    // Prefer instant load from the buffer (skip-buffer behavior).
    const buffered = consumeBufferedRound();
    if (buffered) {
      // The buffer was generated with prefetch=true (no server-side charge).
      // Charge the image cost now that the user is actually using it.
      const ok = await spendCoins(GENERATIONS_IMAGE_COST);
      if (!ok) {
        showErrorMessage('Not enough coins for the next round.');
        return;
      }
      await resetRoundUi();
      applyRound(buffered);
      await refreshCoins?.();
      return;
    }

    // If we're currently buffering, make Next wait (to keep it "instant" once enabled).
    if (isPrefetching || gameState.isPrefetchPending) return;

    // Fallback: generate on demand (user may have to wait).
    // generateAndStartRound calls the server WITHOUT prefetch, so coins are charged there.
    await generateAndStartRound();
  }, [
    applyRound,
    consumeBufferedRound,
    gameState.isPrefetchPending,
    gameState.isRequestPending,
    generateAndStartRound,
    isPrefetching,
    openAuthModal,
    profileLoading,
    refreshCoins,
    resetRoundUi,
    showErrorMessage,
    user,
  ]);

  const handleSkip = useCallback(async () => {
    if (gameState.isRequestPending) return;
    if (profileLoading) return;
    if (!user) {
      openAuthModal();
      return;
    }
    if (!bufferedRound) {
      showErrorMessage('Next image is still buffering…');
      return;
    }

    // Leaderboard: discard the current round timer
    activeTimer.stop();
    roundIdRef.current = null;

    const ok = await spendCoins(GENERATIONS_SKIP_COST);
    if (!ok) {
      showErrorMessage('Not enough coins to skip.');
      return;
    }
    await refreshCoins?.();

    const buffered = consumeBufferedRound();
    if (!buffered) {
      showErrorMessage('Next image is still buffering…');
      return;
    }

    await resetRoundUi();
    applyRound(buffered);
  }, [
    applyRound,
    bufferedRound,
    consumeBufferedRound,
    gameState.isRequestPending,
    openAuthModal,
    profileLoading,
    refreshCoins,
    resetRoundUi,
    showErrorMessage,
    user,
  ]);

  const handleInspect = useCallback(async () => {
    if (gameState.loading || gameState.gameOver || isInspecting) return;
    if (profileLoading) return;
    if (!user) {
      openAuthModal();
      return;
    }

    setIsInspecting(true);

    try {
      // Check if there are absent letters to reveal FIRST (avoid spending coins for nothing).
      const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
      const usedLettersArray = Array.from(usedLetters).map((status) => status.split('-')[0]);
      const unusedLetters = allLetters.filter((letter) => !usedLettersArray.includes(letter));
      const solutionLetters = gameState.solution.toUpperCase().split('');
      const absentLetters = unusedLetters.filter((letter) => !solutionLetters.includes(letter));

      if (absentLetters.length === 0) {
        showErrorMessage('No more absent letters to reveal!');
        return;
      }

      const data = await requestPowerup({
        type: 'inspect',
        word: gameState.solution,
        gameType: 'generations',
      });
      setFreeInspectsRemaining(data.freeInspectsRemaining ?? 0);
      await refreshCoins?.();

      if (data.success) {
        const numToReveal = Math.min(3, absentLetters.length);
        const shuffled = [...absentLetters].sort(() => 0.5 - Math.random());
        const lettersToReveal = shuffled.slice(0, numToReveal);

        const newUsedLetters = new Set(usedLetters);
        lettersToReveal.forEach((letter) => {
          newUsedLetters.add(`${letter}-absent`);
        });
        setUsedLetters(newUsedLetters);
        showErrorMessage(`Revealed absent letters: ${lettersToReveal.join(', ')}`);
      }
    } catch (error) {
      console.error('Error during inspect:', error);
      if (error?.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins to use Inspect!');
      } else if (error?.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else {
        showErrorMessage('Failed to reveal letters');
      }
    } finally {
      setIsInspecting(false);
    }
  }, [
    gameState.solution,
    gameState.loading,
    gameState.gameOver,
    isInspecting,
    profileLoading,
    usedLetters,
    showErrorMessage,
    user,
    openAuthModal,
    refreshCoins,
  ]);

  const handleZoomOut = useCallback(async () => {
    if (gameState.loading || gameState.gameOver || isZoomingOut) return;
    if (profileLoading) return;
    if (!user) {
      openAuthModal();
      return;
    }

    setIsZoomingOut(true);

    try {
      const data = await requestPowerup({
        type: 'zoom_out',
        word: gameState.solution,
        gameType: 'generations',
      });
      setFreeZoomOutsRemaining(data.freeZoomOutsRemaining ?? freeZoomOutsRemaining);
      await refreshCoins?.();

      if (!data?.success) {
        setIsZoomingOut(false);
        return;
      }

      if (zoomOutTimerRef.current) {
        clearTimeout(zoomOutTimerRef.current);
        zoomOutTimerRef.current = null;
      }

      zoomOutTimerRef.current = setTimeout(() => {
        zoomOutTimerRef.current = null;
        if (mounted.current) {
          setIsZoomingOut(false);
        }
      }, 120);
    } catch (error) {
      console.error('Error during zoom out:', error);
      if (error?.code === 'NOT_ENOUGH_COINS') {
        showErrorMessage('Not enough coins to Zoom Out!');
      } else if (error?.code === 'UNAUTHORIZED') {
        openAuthModal();
      } else {
        showErrorMessage('Failed to use Zoom Out');
      }
      setIsZoomingOut(false);
    }
  }, [
    gameState.loading,
    gameState.gameOver,
    gameState.solution,
    isZoomingOut,
    openAuthModal,
    profileLoading,
    refreshCoins,
    showErrorMessage,
    user,
    freeZoomOutsRemaining,
  ]);

  const handleRiddlePanelToggle = useCallback(() => {
    if (profileLoading || gameState.loading || gameState.gameOver || modalState.showModal) return;
    if (!user) {
      openAuthModal();
      return;
    }
    setShowRiddlePanel((prev) => !prev);
  }, [gameState.gameOver, gameState.loading, modalState.showModal, openAuthModal, profileLoading, user]);

  const revealRiddleWithDifficulty = useCallback(
    async (difficulty) => {
      if (
        profileLoading ||
        gameState.loading ||
        gameState.gameOver ||
        modalState.showModal ||
        isLoadingRiddle ||
        !gameState.solution
      ) {
        return;
      }

      if (!user) {
        openAuthModal();
        return;
      }

      const canAfford =
        difficulty === RIDDLE_DIFFICULTY.EASY ? canAffordEasyRiddle : canAffordNormalRiddle;
      if (!canAfford) {
        setRiddleError(
          difficulty === RIDDLE_DIFFICULTY.EASY
            ? 'Not enough coins for an easy riddle.'
            : 'Not enough coins for the next riddle.'
        );
        return;
      }

      setIsLoadingRiddle(true);
      setRiddleError('');
      setServerNotEnoughFor(null);

      try {
        const data = await fetchRiddle(gameState.solution, {
          difficulty,
          guessRow: gameState.currentRow,
        });
        const riddle = data?.riddle || '';
        if (!riddle) {
          setRiddleError('No riddle returned. Try again.');
          return;
        }

        const newHistory = capRiddleHistory([...riddleHistory, riddle]);
        const newCount = riddleUseCount + 1;

        setRiddleText(riddle);
        setRiddleHistory(newHistory);
        setRiddleUseCount(newCount);
        if (difficulty === RIDDLE_DIFFICULTY.EASY) {
          setFreeEasyRiddlesRemaining(data?.freeEasyRiddlesRemaining ?? freeEasyRiddlesRemaining);
        }

        // Persist to DB-backed game state so it survives navigation + multi-device.
        setGameState((prev) => ({
          ...prev,
          riddleSession: {
            word: prev.solution,
            useCount: newCount,
            riddles: newHistory,
          },
        }));
        await refreshCoins?.();
      } catch (error) {
        if (error?.code === 'NOT_ENOUGH_COINS') {
          setServerNotEnoughFor(difficulty);
          setRiddleError(
            difficulty === RIDDLE_DIFFICULTY.EASY
              ? 'Not enough coins for an easy riddle.'
              : 'Not enough coins for the next riddle.'
          );
        } else if (error?.code === 'UNAUTHORIZED') {
          openAuthModal();
        } else {
          setRiddleError('Failed to generate a riddle. Try again.');
        }
      } finally {
        setIsLoadingRiddle(false);
      }
    },
    [
      canAffordEasyRiddle,
      canAffordNormalRiddle,
      capRiddleHistory,
      gameState.gameOver,
      gameState.loading,
      gameState.solution,
      isLoadingRiddle,
      modalState.showModal,
      openAuthModal,
      profileLoading,
      refreshCoins,
      riddleHistory,
      riddleUseCount,
      user,
    ]
  );

  const handleRevealNormalRiddle = useCallback(
    () => revealRiddleWithDifficulty(RIDDLE_DIFFICULTY.HARD),
    [revealRiddleWithDifficulty]
  );
  const handleRevealEasyRiddle = useCallback(
    () => revealRiddleWithDifficulty(RIDDLE_DIFFICULTY.EASY),
    [revealRiddleWithDifficulty]
  );

  const handleGameOver = useCallback(
    (isWin, finalGuesses) => {
      // Leaderboard: stop timer and record result, capture points awarded
      const activeMs = activeTimer.stop();
      const reportRoundId = roundIdRef.current;
      if (reportRoundId) {
        finishRound({
          roundId: reportRoundId,
          won: isWin,
          guessesUsed: gameState.currentRow + 1,
          activeTimeMs: activeMs,
        }).then((result) => {
          if (result) {
            const updates = {};
            if (result.pointsAwarded) {
              updates.pointsAwarded = result.pointsAwarded;
              updates.firstGameBonus = result.firstGameBonus;
              setSeasonPoints((prev) => (prev ?? 0) + result.pointsAwarded);
              setLifetimePoints((prev) => (prev ?? 0) + result.pointsAwarded);
            }
            // Server is authoritative for coins (daily-cap enforced).
            if (typeof result.coinsAwarded === 'number') {
              updates.rewardCoins = result.coinsAwarded;
            }
            if (Object.keys(updates).length > 0) {
              setModalState((prev) => ({ ...prev, ...updates }));
            }
            // Refresh the coin balance so the UI header stays in sync.
            refreshCoins?.();
          }
        });
        roundIdRef.current = null;
      }

      // Reset any existing definition data (prevents stale content across rounds)
      setDefinition(null);
      setShowDefinition(false);
      setIsLoadingDefinition(false);

      if (isWin) setConfettiState({ showConfetti: true });

      // Prevent duplicate awards if something triggers game over twice for the same round.
      const roundKey = `${String(gameState.solution || '')}|${String(gameState.imageUrl || '')}`;
      if (rewardGuardRef.current.key !== roundKey) {
        rewardGuardRef.current = { key: roundKey, rewarded: false };
      }

      // Client-side estimate for immediate display (server value overrides in .then above)
      const guessesUsed = gameState.currentRow + 1;
      const rewardCoins = isWin
        ? getWinRewardCoins('generations', guessesUsed)
        : getLossRewardCoins();

      if (user) {
        setStats((prevStats) => {
          const currentStats = normalizeGenerationsStats(prevStats);
          const nextStats = isWin
            ? {
                ...currentStats,
                guesses: {
                  ...currentStats.guesses,
                  [guessesUsed]: (currentStats.guesses[guessesUsed] || 0) + 1,
                },
                currentStreak: currentStats.currentStreak + 1,
                maxStreak: Math.max(currentStats.maxStreak, currentStats.currentStreak + 1),
              }
            : {
                ...currentStats,
                losses: currentStats.losses + 1,
                currentStreak: 0,
                guesses: { ...currentStats.guesses },
              };

          try {
            localStorage.setItem(GENERATIONS_STATS_STORAGE_KEY, JSON.stringify(nextStats));
          } catch (error) {
            console.error('Error saving generations stats:', error);
          }

          return nextStats;
        });
      }

      // Coins are now awarded server-side by game-round handleFinish.
      // No client-side addCoins call needed.

      setModalState({
        showModal: true,
        title: isWin ? 'Congrats, You Win!' : `Round Over! The word was: ${gameState.solution.toUpperCase()}`,
        isWin,
        rewardCoins,
        // Capture the zoom the player was seeing before we reset it below.
        shareZoomLevel: gameState.zoomLevel,
        shareZoomOrigin: gameState.zoomOrigin,
        reportRoundId,
      });
      setGameState((prev) => ({
        ...prev,
        // Include the final guess so share/copy results have the complete board.
        guesses: finalGuesses || prev.guesses,
        gameOver: true,
        zoomLevel: MIN_ZOOM,
        isChecking: false,
      }));
    },
    [activeTimer, gameState.currentRow, gameState.imageUrl, gameState.solution, refreshCoins, user]
  );

  const onKeyPress = useCallback(
    (key) => {
      if (gameState.isRequestPending) return;
      if (!gameState.solution || !gameState.imageUrl) return;
      if (previewImageUrl) return;
      if (gameState.gameOver || modalState.showModal) return;

      if (key === 'ENTER') {
        if (gameState.currentGuess.length !== 5) {
          showErrorMessage('Word must be 5 letters');
          return;
        }
        const guessLower = gameState.currentGuess.toLowerCase();
        if (!validWords.has(guessLower)) {
          showErrorMessage('Not in word list');
          return;
        }

        const newGuesses = [...gameState.guesses];
        newGuesses[gameState.currentRow] = gameState.currentGuess;

        const newUsedLetters = new Set(usedLetters);
        const currentGuess = gameState.currentGuess.toUpperCase();
        const solution = gameState.solution.toUpperCase();
        const remainingLetters = {};
        [...solution].forEach((letter) => {
          remainingLetters[letter] = (remainingLetters[letter] || 0) + 1;
        });
        [...currentGuess].forEach((letter, i) => {
          if (solution[i] === letter) {
            newUsedLetters.add(`${letter}-correct`);
            remainingLetters[letter]--;
          }
        });
        [...currentGuess].forEach((letter, i) => {
          if (solution[i] !== letter) {
            if (remainingLetters[letter] > 0) {
              newUsedLetters.add(`${letter}-present`);
              remainingLetters[letter]--;
            } else {
              newUsedLetters.add(`${letter}-absent`);
            }
          }
        });
        setUsedLetters(newUsedLetters);

        if (guessLower === gameState.solution.toLowerCase()) {
          handleGameOver(true, newGuesses);
          return;
        }

        if (gameState.currentRow >= gameState.guesses.length - 1) {
          handleGameOver(false, newGuesses);
          return;
        }

        const newZoomLevel = Math.max(MIN_ZOOM, gameState.zoomLevel - ZOOM_DECREASE);
        setGameState((prev) => ({
          ...prev,
          guesses: newGuesses,
          currentGuess: '',
          currentRow: prev.currentRow + 1,
          zoomLevel: newZoomLevel,
          isChecking: true,
        }));

        setTimeout(() => {
          if (mounted.current) {
            setGameState((prev) => ({ ...prev, isChecking: false }));
          }
        }, WIN_MODAL_DELAY);
      } else if (key === '⌫') {
        setGameState((prev) => ({
          ...prev,
          currentGuess: prev.currentGuess.slice(0, -1),
        }));
      } else if (gameState.currentGuess.length < 5 && /^[A-Z]$/.test(key)) {
        setGameState((prev) => ({
          ...prev,
          currentGuess: prev.currentGuess + key,
        }));
      }
    },
    [
      gameState,
      handleGameOver,
      modalState.showModal,
      previewImageUrl,
      showErrorMessage,
      usedLetters,
      validWords,
    ]
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') onKeyPress('ENTER');
      else if (e.key === 'Backspace') onKeyPress('⌫');
      else if (/^[a-zA-Z]$/.test(e.key)) onKeyPress(e.key.toUpperCase());
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKeyPress]);

  const shouldShowStartOverlay = useMemo(() => {
    // If a game round is already active (solution + image loaded), keep it visible
    // even during transient auth-state transitions. This prevents the start screen
    // from flashing when Supabase auto-refreshes the token on tab focus/visibility.
    if (gameState.solution && gameState.imageUrl) return false;

    // Keep the start CTA hidden while we are still checking whether there is a
    // resumable saved round. Prevents accidental replacement of an existing round.
    if (isRestoreCheckPending) return false;
    if (profileLoading) return true;
    if (!user) return true;
    return !gameState.solution || !gameState.imageUrl;
  }, [gameState.imageUrl, gameState.solution, isRestoreCheckPending, profileLoading, user]);

  const shouldShowRestoreOverlay = useMemo(() => {
    if (!user?.id) return false;
    if (gameState.solution && gameState.imageUrl) return false;
    return isRestoreCheckPending;
  }, [gameState.imageUrl, gameState.solution, isRestoreCheckPending, user?.id]);

  const skipLabelOverride = useMemo(() => {
    const isInActiveRound = Boolean(gameState.solution && gameState.imageUrl) && !gameState.gameOver;
    if (!isInActiveRound) return undefined;
    if (bufferedRound) return undefined;
    if (isPrefetching || gameState.isPrefetchPending) return undefined;
    if (canAffordBufferSkip) return undefined;
    return 'Not enough coins';
  }, [
    bufferedRound,
    canAffordBufferSkip,
    gameState.gameOver,
    gameState.imageUrl,
    gameState.isPrefetchPending,
    gameState.solution,
    isPrefetching,
  ]);

  const displayedZoomLevel = isZoomingOut ? MIN_ZOOM : gameState.zoomLevel;

  return (
    <div
      className={`game generations-game zi-game-shell ${
        mobileSideBySide ? 'generations-game--split' : ''
      }`.trim()}
    >
      {errorState.showError && <div className="error-popup">{errorState.errorMessage}</div>}
      {confettiState.showConfetti && (
        <Confetti width={window.innerWidth} height={window.innerHeight} />
      )}
      {modalState.showModal && (
        <div className="modal-overlay">
          <GenerationsGameModal
            title={modalState.title}
            isWin={modalState.isWin}
            solution={gameState.solution}
            imageUrl={gameState.imageUrl}
            reportRoundId={modalState.reportRoundId ?? null}
            rewardCoins={modalState.rewardCoins ?? null}
            pointsAwarded={modalState.pointsAwarded ?? 0}
            firstGameBonus={modalState.firstGameBonus ?? false}
            zoomLevelForShare={modalState.shareZoomLevel ?? null}
            zoomOriginForShare={modalState.shareZoomOrigin ?? null}
            guesses={gameState.guesses}
            stats={stats}
            user={user}
            onNextRound={nextRound}
            countdown={countdown}
            disableNext={Boolean((isPrefetching || gameState.isPrefetchPending) && !bufferedRound)}
            nextLabel={
              (isPrefetching || gameState.isPrefetchPending) && !bufferedRound
                ? 'Buffering next…'
                : 'Next Round'
            }
            showDefinition={showDefinition}
            definition={definition}
            isLoadingDefinition={isLoadingDefinition}
            toggleDefinition={toggleDefinition}
          />
        </div>
      )}
      {previewImageUrl && (
        <ImagePreviewModal
          imageUrl={previewImageUrl}
          zoomLevel={displayedZoomLevel}
          zoomOrigin={gameState.zoomOrigin}
          alt={
            gameState.solution
              ? `Expanded generated image for ${gameState.solution.toUpperCase()}`
              : 'Expanded generated image'
          }
          onClose={closeImagePreview}
        />
      )}

      <div className="game-top-container">
        <div
          className={`generations-top-main ${
            mobileSideBySide ? 'generations-top-main--sideBySide' : ''
          }`.trim()}
        >
          <GameImage
            imageLoading={imageLoading}
            imageUrl={gameState.imageUrl}
            zoomLevel={displayedZoomLevel}
            zoomOrigin={gameState.zoomOrigin}
            isChecking={gameState.isChecking}
            disableTransition={false}
            waitingForFallback={false}
            solution={gameState.solution || 'WORD'}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
            onImageClick={
              imageLoading || gameState.loading || modalState.showModal || isZoomingOut
                ? undefined
                : openImagePreview
            }
          />

          <Grid
            // Same iOS Safari workaround as Unlimited: when split/stack changes, Safari may
            // shrink the container but keep a stale internal grid layout (scrollWidth > width)
            // until a later "real" reflow (e.g., app background/foreground). Remounting the
            // Grid forces a fresh layout pass immediately.
            key={`gen-${gameState.solution || 'pending'}-${mobileSideBySide ? 'split' : 'stack'}`}
            guesses={gameState.guesses}
            currentGuess={gameState.currentGuess}
            currentRow={gameState.currentRow}
            solution={gameState.solution}
            isTypingEnabled={
              !gameState.gameOver &&
              !gameState.loading &&
              !modalState.showModal &&
              Boolean(gameState.solution)
            }
          />
        </div>

        <GenerationsGameControls
          onSkip={handleSkip}
          onInspect={handleInspect}
          onZoomOut={handleZoomOut}
          onRiddleOpen={handleRiddlePanelToggle}
          skipCostCoins={GENERATIONS_SKIP_COST}
          isSkipBuffering={Boolean(
            (isPrefetching || gameState.isPrefetchPending) && !bufferedRound
          )}
          skipLabelOverride={skipLabelOverride}
          freeInspectsRemaining={freeInspectsRemaining}
          freeZoomOutsRemaining={freeZoomOutsRemaining}
          disableSkip={
            profileLoading ||
            gameState.loading ||
            modalState.showModal ||
            gameState.isRequestPending ||
            !gameState.solution ||
            !gameState.imageUrl ||
            gameState.gameOver ||
            !bufferedRound
          }
          disableInspect={
            profileLoading ||
            modalState.showModal ||
            gameState.isRequestPending ||
            gameState.loading ||
            gameState.gameOver ||
            isInspecting ||
            !hasRevealableLetters
          }
          disableZoomOut={
            profileLoading ||
            modalState.showModal ||
            gameState.isRequestPending ||
            gameState.loading ||
            gameState.gameOver ||
            isZoomingOut
          }
          disableRiddle={
            profileLoading ||
            gameState.loading ||
            gameState.gameOver ||
            modalState.showModal ||
            gameState.isRequestPending ||
            !gameState.solution
          }
          isRiddleOpen={showRiddlePanel}
        />
      </div>

      <RiddlePanel
        isOpen={showRiddlePanel}
        onClose={handleRiddlePanelToggle}
        onRevealNormal={handleRevealNormalRiddle}
        onRevealEasy={handleRevealEasyRiddle}
        riddleText={riddleText}
        riddles={riddleHistory}
        isLoading={isLoadingRiddle}
        canAffordNormal={canAffordNormalRiddle}
        normalCost={nextRiddleCostNormal}
        canAffordEasy={canAffordEasyRiddle}
        easyCost={nextRiddleCostEasy}
        freeEasyRiddlesRemaining={freeEasyRiddlesRemaining}
        useCount={riddleUseCount}
        errorMessage={riddleError}
      />

      <div className="game-bottom-container">
        <Keyboard onKeyPress={onKeyPress} usedLetters={usedLetters} />
      </div>

      {shouldShowRestoreOverlay && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Restoring your round...</h2>
            <p style={{ color: 'var(--text-muted)' }}>
              Please wait while we load your saved Generations game.
            </p>
          </div>
        </div>
      )}

      {shouldShowStartOverlay && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Zoomed In Generations</h2>
            <p style={{ color: 'var(--text-muted)' }}>
              {user ? (
                <>
                  Generate a brand-new AI image for <strong>{GENERATIONS_IMAGE_COST}</strong>{' '}
                  coins, then guess the 5-letter word.
                </>
              ) : (
                <>Generate a brand-new AI image, then guess the 5-letter word.</>
              )}
            </p>
            {!user ? (
              <div className="modal-buttons">
                <button className="modal-button" onClick={openAuthModal}>
                  Sign In For Free to Play
                </button>
                <button className="modal-button" onClick={handleMaybeLater}>
                  Maybe later
                </button>
              </div>
            ) : (
              <>
                <div className="modal-buttons">
                  <button
                    className="modal-button"
                    onClick={generateAndStartRound}
                    disabled={
                      !canAfford ||
                      gameState.isRequestPending ||
                      !dictionaryLoaded ||
                      isRestoreCheckPending
                    }
                    style={{
                      opacity: !canAfford || isRestoreCheckPending ? 0.6 : 1,
                    }}
                  >
                    {gameState.isRequestPending ? 'Generating…' : `Start Round (-${GENERATIONS_IMAGE_COST} coins)`}
                  </button>
                  <button
                    className="modal-button copy-button"
                    onClick={handleMaybeLater}
                    disabled={gameState.isRequestPending}
                  >
                    Back Home
                  </button>
                </div>
                {!canAfford && (
                  <p style={{ marginTop: '8px', color: '#ffb3b3' }}>
                    Not enough coins.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ZoomedInGenerationsGame;
